import { useEffect, useState } from 'react'

import { cn } from './cn'
import { useOnlineStatus } from './useOnlineStatus'

const RECONNECT_DISPLAY_MS = 3_000

export interface OfflineBannerProps {
  offlineMessage?: string
  onlineMessage?: string
  /** Position du bandeau. Défaut : 'top'. */
  position?: 'top' | 'bottom'
}

/**
 * Bandeau d'état réseau — règle UX CLAUDE.md §10 :
 *   « bandeau rouge en haut si hors-ligne, vert au retour de connexion ».
 * Se masque automatiquement 3s après le retour en ligne.
 */
export function OfflineBanner({
  offlineMessage = 'Hors-ligne — les modifications seront synchronisées au retour de connexion',
  onlineMessage = 'Connexion rétablie',
  position = 'top',
}: OfflineBannerProps) {
  const online = useOnlineStatus()
  const [wasOffline, setWasOffline] = useState<boolean>(false)
  const [showReconnected, setShowReconnected] = useState<boolean>(false)

  useEffect(() => {
    if (!online) {
      setWasOffline(true)
      setShowReconnected(false)
      return
    }
    if (online && wasOffline) {
      setShowReconnected(true)
      const t = setTimeout(() => {
        setShowReconnected(false)
        setWasOffline(false)
      }, RECONNECT_DISPLAY_MS)
      return () => clearTimeout(t)
    }
    return undefined
  }, [online, wasOffline])

  if (online && !showReconnected) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed inset-x-0 z-50 px-4 py-2 text-center text-sm font-medium text-white shadow-md',
        position === 'top' ? 'top-0' : 'bottom-0',
        online ? 'bg-online' : 'bg-offline',
      )}
    >
      {online ? onlineMessage : offlineMessage}
    </div>
  )
}
